<template>
  <!-- Two-panel hero: fills BaseModal's `content` slot, so the shared chrome does not apply. -->
  <BaseModal
    :model-value="open"
    :title="welcomeHeadline"
    max-width-class="max-w-lg sm:max-w-3xl"
    panel-class="ring-1 ring-focus-ink/5 shadow-2xl"
    :show-close-button="false"
    @close="close"
  >
    <template #content>
      <div class="flex flex-col sm:flex-row sm:min-h-[22rem]">
        <!-- Visual / hero -->
        <div
          class="min-h-[11rem] overflow-hidden relative shrink-0 sm:max-w-none sm:min-h-full sm:w-[42%]"
        >
          <div
            class="absolute bg-gradient-to-br dark:from-primary-700 dark:to-primary-950 dark:via-primary-800 from-primary-600 inset-0 to-primary-900 via-primary-700"
          />

          <div
            class="absolute bg-[image:var(--surface-welcome-hero-glow)] dark:opacity-30 inset-0 opacity-40 pointer-events-none"
          />

          <img
            v-if="heroSrc"
            :src="heroSrc"
            alt=""
            class="absolute h-full inset-0 object-cover w-full"
          />

          <div
            v-else
            class="absolute inset-0 pointer-events-none select-none"
            aria-hidden="true"
          >
            <Pizza
              class="-left-14 -top-10 absolute h-40 rotate-[-14deg] sm:-left-16 sm:-top-12 sm:h-48 sm:w-48 text-content-inverse/[0.07] w-40"
              :stroke-width="0.75"
            />

            <ChefHat
              class="-bottom-8 -right-10 absolute h-36 rotate-[18deg] sm:-bottom-10 sm:-right-12 sm:h-44 sm:w-44 text-content-inverse/[0.09] w-36"
              :stroke-width="0.75"
            />

            <Croissant
              class="-translate-x-1/2 -translate-y-1/2 absolute h-28 left-1/2 rotate-[8deg] sm:h-32 sm:w-32 text-content-inverse/[0.11] top-1/2 w-28"
              :stroke-width="0.75"
            />

            <Soup
              class="-left-8 absolute bottom-6 h-24 rotate-[8deg] sm:bottom-10 sm:h-28 sm:w-28 text-content-inverse/[0.075] w-24"
              :stroke-width="0.75"
            />

            <Coffee
              class="-right-4 absolute h-20 rotate-[22deg] sm:h-24 sm:top-6 sm:w-24 text-content-inverse/[0.07] top-4 w-20"
              :stroke-width="0.75"
            />
          </div>
        </div>

        <!-- Copy + actions -->
        <div
          class="flex flex-1 flex-col justify-center pb-6 pt-5 px-6 relative sm:pl-8 sm:px-10 sm:py-10"
        >
          <button
            type="button"
            class="absolute cursor-pointer dark:hover:bg-surface-canvas/10 dark:hover:text-content-level-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 hover:bg-surface-level-100 hover:text-content-level-600 p-2 right-3 rounded-full text-content-level-400 top-3 transition"
            :aria-label="$t('common.actions.close')"
            @click="close"
          >
            <XMarkIcon
              class="h-5 w-5"
              aria-hidden="true"
            />
          </button>

          <p
            class="dark:text-content-inverse font-semibold leading-7 pr-10 sm:leading-8 sm:text-2xl text-content-level-900 text-xl tracking-tight"
          >
            {{ welcomeHeadline }}
          </p>

          <div
            class="dark:text-content-level-400 leading-6 mt-3 space-y-3 text-content-level-600 text-sm"
          >
            <p>
              {{ $t('dashboard.messages.weReExcitedToHaveYouOnBoard') }}
            </p>

            <p>
              {{
                $t(
                  'dashboard.messages.yourPartnerDashboardIsReadyNowLetSGetYourBusinessSetUpSoCustomersCanDiscoverYouAndStartPlacingOrders'
                )
              }}
            </p>
          </div>

          <ul
            class="dark:text-content-level-400 mt-5 space-y-2.5 text-content-level-600 text-sm"
            role="list"
          >
            <li class="flex gap-2.5">
              <CheckCircleIcon
                class="dark:text-primary-400 h-5 mt-0.5 shrink-0 text-primary-500 w-5"
                aria-hidden="true"
              />

              <span>
                {{ $t('dashboard.labels.setUpYourFirstStore') }}
              </span>
            </li>

            <li class="flex gap-2.5">
              <CheckCircleIcon
                class="dark:text-primary-400 h-5 mt-0.5 shrink-0 text-primary-500 w-5"
                aria-hidden="true"
              />

              <span>
                {{ $t('dashboard.labels.publishMenusAndPricing') }}
              </span>
            </li>

            <li class="flex gap-2.5">
              <CheckCircleIcon
                class="dark:text-primary-400 h-5 mt-0.5 shrink-0 text-primary-500 w-5"
                aria-hidden="true"
              />

              <span>
                {{ $t('dashboard.labels.trackOrdersAsTheyComeIn') }}
              </span>
            </li>
          </ul>

          <div class="mt-8">
            <button
              type="button"
              class="bg-primary cursor-pointer dark:ring-offset-focus-level-900 flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/40 font-semibold hover:bg-primary-600 justify-center px-4 py-2.5 rounded-lg shadow-sm text-content-inverse text-sm transition w-full"
              @click="close"
            >
              {{ $t('dashboard.labels.getStarted') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import { CheckCircleIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import { ChefHat, Coffee, Croissant, Pizza, Soup } from '@lucide/vue'

const props = withDefaults(
  defineProps<{
    /** Whether the welcome modal is visible. */
    open: boolean
    /** Optional photo URL for the left panel; defaults to built-in illustration. */
    heroSrc?: string
    /** First name for the headline (e.g. “Welcome to Mealz, Alex”). */
    userName?: string
  }>(),
  { heroSrc: '', userName: '' }
)

const emit = defineEmits<{
  close: []
}>()

const welcomeHeadline = computed(() => {
  const name = props.userName?.trim()
  if (name) return `Welcome to Mealz, ${name}`
  return 'Welcome to Mealz'
})

function close() {
  emit('close')
}
</script>
